import { useCallback, useEffect, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { apiFetch } from "@/lib/api-fetch";
import { Bug, Lightbulb, AlertTriangle, HelpCircle, CheckCircle2, Clock, MessageCircleQuestion, ExternalLink } from "lucide-react";

type BugReport = {
  id: number;
  title: string;
  description: string;
  category: "bug" | "feature" | "improvement" | "question";
  priority: "low" | "medium" | "high" | "critical";
  status: "open" | "in_progress" | "resolved" | "closed";
  page_url: string | null;
  browser_info: string | null;
  created_at: string;
  resolved_at: string | null;
  user_name: string | null;
  user_email: string | null;
};

const CATEGORY_META: Record<string, { label: string; icon: typeof Bug; className: string }> = {
  bug:         { label: "Bug",         icon: Bug,                   className: "text-red-500" },
  feature:     { label: "Feature",     icon: Lightbulb,             className: "text-amber-500" },
  improvement: { label: "Improvement", icon: AlertTriangle,         className: "text-blue-500" },
  question:    { label: "Question",    icon: MessageCircleQuestion, className: "text-green-500" },
};

const PRIORITY_META: Record<string, { label: string; className: string }> = {
  low:      { label: "Low",      className: "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300" },
  medium:   { label: "Medium",   className: "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-400" },
  high:     { label: "High",     className: "bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-400" },
  critical: { label: "Critical", className: "bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-400" },
};

const STATUS_META: Record<string, { label: string; icon: typeof Clock; className: string }> = {
  open:        { label: "Open",        icon: Clock,        className: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400" },
  in_progress: { label: "In Progress", icon: AlertTriangle, className: "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400" },
  resolved:    { label: "Resolved",    icon: CheckCircle2, className: "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400" },
  closed:      { label: "Closed",      icon: HelpCircle,   className: "bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-400" },
};

function formatDate(iso: string) {
  return new Date(iso).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
}

export default function BugReportsAdminPage() {
  const { token } = useAuth();
  const [reports, setReports] = useState<BugReport[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [expandedId, setExpandedId] = useState<number | null>(null);

  const fetchReports = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiFetch("/api/bug-reports", { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) setReports(await res.json());
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { fetchReports(); }, [fetchReports]);

  const updateStatus = async (id: number, status: string) => {
    const res = await apiFetch(`/api/bug-reports/${id}`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      body: JSON.stringify({ status }),
    });
    if (res.ok) {
      setReports((prev) => prev.map((r) => (r.id === id ? { ...r, status: status as any, resolved_at: status === "resolved" ? new Date().toISOString() : null } : r)));
    }
  };

  const filtered = statusFilter === "all" ? reports : reports.filter((r) => r.status === statusFilter);
  const counts = {
    open: reports.filter((r) => r.status === "open").length,
    in_progress: reports.filter((r) => r.status === "in_progress").length,
    resolved: reports.filter((r) => r.status === "resolved").length,
  };

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <div className="flex items-start justify-between mb-6">
        <div>
          <h1 className="heading-display text-3xl text-foreground">User Feedback</h1>
          <p className="text-sm text-muted-foreground mt-1">Bug reports, feature requests, questions from your team</p>
        </div>
      </div>

      {/* Stat cards */}
      <div className="grid grid-cols-3 gap-3 mb-6">
        <button
          onClick={() => setStatusFilter(statusFilter === "open" ? "all" : "open")}
          className={`text-left p-4 rounded-xl border transition-colors ${statusFilter === "open" ? "border-blue-400 bg-blue-50/50 dark:bg-blue-950/20" : "border-border bg-card hover:bg-muted/30"}`}
        >
          <div className="flex items-center gap-2 text-xs font-semibold text-blue-600 dark:text-blue-400 uppercase tracking-wide mb-1"><Clock size={12} /> Open</div>
          <div className="text-2xl font-bold text-foreground tabular-nums">{counts.open}</div>
        </button>
        <button
          onClick={() => setStatusFilter(statusFilter === "in_progress" ? "all" : "in_progress")}
          className={`text-left p-4 rounded-xl border transition-colors ${statusFilter === "in_progress" ? "border-amber-400 bg-amber-50/50 dark:bg-amber-950/20" : "border-border bg-card hover:bg-muted/30"}`}
        >
          <div className="flex items-center gap-2 text-xs font-semibold text-amber-600 dark:text-amber-400 uppercase tracking-wide mb-1"><AlertTriangle size={12} /> In Progress</div>
          <div className="text-2xl font-bold text-foreground tabular-nums">{counts.in_progress}</div>
        </button>
        <button
          onClick={() => setStatusFilter(statusFilter === "resolved" ? "all" : "resolved")}
          className={`text-left p-4 rounded-xl border transition-colors ${statusFilter === "resolved" ? "border-green-400 bg-green-50/50 dark:bg-green-950/20" : "border-border bg-card hover:bg-muted/30"}`}
        >
          <div className="flex items-center gap-2 text-xs font-semibold text-green-600 dark:text-green-400 uppercase tracking-wide mb-1"><CheckCircle2 size={12} /> Resolved</div>
          <div className="text-2xl font-bold text-foreground tabular-nums">{counts.resolved}</div>
        </button>
      </div>

      {loading ? (
        <div className="text-center py-12 text-muted-foreground">Loading…</div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground">No reports{statusFilter !== "all" ? ` with status "${statusFilter}"` : ""} yet.</div>
      ) : (
        <div className="space-y-2">
          {filtered.map((r) => {
            const catMeta = CATEGORY_META[r.category] || CATEGORY_META.bug;
            const priMeta = PRIORITY_META[r.priority] || PRIORITY_META.medium;
            const statMeta = STATUS_META[r.status] || STATUS_META.open;
            const CatIcon = catMeta.icon;
            const StatIcon = statMeta.icon;
            const isExpanded = expandedId === r.id;
            return (
              <div key={r.id} className="bg-card border border-border rounded-xl overflow-hidden">
                <button
                  type="button"
                  onClick={() => setExpandedId(isExpanded ? null : r.id)}
                  className="w-full text-left p-4 hover:bg-muted/30 transition-colors flex items-start gap-3"
                >
                  <CatIcon size={18} className={`${catMeta.className} shrink-0 mt-0.5`} />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-start gap-2 flex-wrap">
                      <span className="font-semibold text-foreground">{r.title}</span>
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide ${priMeta.className}`}>{priMeta.label}</span>
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide ${statMeta.className}`}><StatIcon size={10} /> {statMeta.label}</span>
                    </div>
                    <div className="text-xs text-muted-foreground mt-1">
                      {r.user_name || "Anonymous"} &middot; {formatDate(r.created_at)}
                      {r.page_url && <> &middot; <span className="font-mono">{r.page_url.length > 40 ? r.page_url.slice(0, 40) + "…" : r.page_url}</span></>}
                    </div>
                  </div>
                </button>
                {isExpanded && (
                  <div className="border-t border-border p-4 bg-muted/20 space-y-3">
                    <div>
                      <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-1">Description</div>
                      <p className="text-sm text-foreground whitespace-pre-wrap">{r.description}</p>
                    </div>
                    {r.page_url && (
                      <div>
                        <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-1">Page</div>
                        <a href={r.page_url} target="_blank" rel="noreferrer" className="text-sm text-forest-600 dark:text-forest-400 hover:underline inline-flex items-center gap-1 break-all">
                          {r.page_url} <ExternalLink size={12} />
                        </a>
                      </div>
                    )}
                    {r.browser_info && (
                      <div>
                        <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-1">Browser</div>
                        <p className="text-xs text-muted-foreground font-mono">{r.browser_info}</p>
                      </div>
                    )}
                    <div className="flex gap-2 pt-2">
                      {r.status !== "open" && (
                        <button onClick={() => updateStatus(r.id, "open")} className="px-3 py-1.5 text-xs font-semibold rounded-lg bg-blue-100 text-blue-700 hover:bg-blue-200 dark:bg-blue-900/30 dark:text-blue-400">Mark as Open</button>
                      )}
                      {r.status !== "in_progress" && (
                        <button onClick={() => updateStatus(r.id, "in_progress")} className="px-3 py-1.5 text-xs font-semibold rounded-lg bg-amber-100 text-amber-700 hover:bg-amber-200 dark:bg-amber-900/30 dark:text-amber-400">In Progress</button>
                      )}
                      {r.status !== "resolved" && (
                        <button onClick={() => updateStatus(r.id, "resolved")} className="px-3 py-1.5 text-xs font-semibold rounded-lg bg-green-100 text-green-700 hover:bg-green-200 dark:bg-green-900/30 dark:text-green-400">Mark Resolved</button>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
